/**
 * HTTP client for patient-graph ingest APIs via the shared api-client.
 *
 * @module patientGraphService
 */
import { api } from "@risalabs_frontend_org/api-client";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import {
  IngestInPatientGraphPayload,
  IngestInPatientGraphResponse,
  PmgIngestionExistsResponse,
} from "./types";

/**
 * Builds GET `/medical/patient-graph/patients/{mrn}/exists` with `org_id`.
 *
 * @param patientMrn - Patient medical record number substituted into the path
 * @param orgId - Organization id sent as `org_id`
 * @returns Fully-qualified exists URL
 */
const buildPmgIngestionExistsUrl = (
  patientMrn: string,
  orgId: string,
): string => {
  const path = API_ENDPOINTS.PATIENT_GRAPH_INGESTION_STATUS.replace(
    "$mrn",
    encodeURIComponent(patientMrn),
  );
  const searchParams = new URLSearchParams({ org_id: orgId });
  return `${path}?${searchParams.toString()}`;
};

/**
 * Service for patient-graph ingest and ingestion-status APIs.
 */
export class PatientGraphService {
  /**
   * Posts a PA order to the backend ingest-pa-order endpoint.
   *
   * Bearer token is injected by {@link api.post}.
   *
   * @param payload - Order id, org id, and patient MRN
   * @param signal - Optional abort signal for in-flight cancellation
   * @returns Ingest outcome (`ingested`, `skipped`, or `failed`)
   * @throws Error when auth is missing or the HTTP request fails
   */
  static async ingestPaOrder(
    payload: IngestInPatientGraphPayload,
    signal?: AbortSignal,
  ): Promise<IngestInPatientGraphResponse> {
    return api.post<IngestInPatientGraphPayload, IngestInPatientGraphResponse>(
      API_ENDPOINTS.INGEST_IN_PATIENT_GRAPH,
      payload,
      signal,
    );
  }

  /**
   * Checks whether PMG data already exists for a patient MRN in an org.
   *
   * Bearer token is injected by {@link api.get}.
   *
   * @param patientMrn - Patient medical record number for the path
   * @param orgId - Organization id sent as the `org_id` query parameter
   * @param signal - Optional abort signal for in-flight cancellation
   * @returns Existence payload including `exists` and `ingested_at`
   * @throws Error when auth is missing or the HTTP request fails
   */
  static async checkIfPmgIngestionExists(
    patientMrn: string,
    orgId: string,
    signal?: AbortSignal,
  ): Promise<PmgIngestionExistsResponse> {
    return api.get<PmgIngestionExistsResponse>(
      buildPmgIngestionExistsUrl(patientMrn, orgId),
      signal,
    );
  }
}
